import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  getSecureItem,
  setSecureItem,
  removeSecureItem,
} from "utils/SecureStorage";
import { ROLE_KEYS } from "utils/useRoles";
import Swal from "sweetalert2";
import { resetPermissionCache } from "utils/permissionManager";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Resolve current role from roles + role_id
const resolveCurrentRole = () => {
  const roleId = Number(getSecureItem("role_id"));

  switch (roleId) {
    case 1:
      return ROLE_KEYS.SUPER_ADMIN;
    case 2:
      return ROLE_KEYS.ADMIN;
    case 3:
      return ROLE_KEYS.MANAGER;
    case 4:
      return ROLE_KEYS.STAFF;
    case 5:
      return ROLE_KEYS.TENANT;
    default:
      return null;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authTokens, setAuthTokens] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize on mount
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedUser = getSecureItem("currentUser");
        const accessToken = getSecureItem("accessToken");
        const refreshToken = getSecureItem("refreshToken");

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        }

        if (accessToken) {
          setAuthTokens({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
        }

        const resolvedRole = resolveCurrentRole();
        setCurrentRole(resolvedRole);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Initialization Error",
          text: "Failed to initialize authentication. Please log in again.",
        });
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    const handleRoleChange = (event) => {
      if (event.key === "role_id") {
        const resolvedRole = resolveCurrentRole();
        setCurrentRole(resolvedRole);
      }
    };

    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  // Multi-tab logout sync
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "logout") {
        setUser(null);
        setAuthTokens(null);
        setCurrentRole(null);
        navigate("/sign-in", { replace: true });
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [navigate]);

  const login = (data) => {
    const { access_token, refresh_token, user: userData } = data;
    const role_id = userData?.role_id;

    // Save to storage
    setSecureItem("accessToken", access_token);
    setSecureItem("refreshToken", refresh_token);
    setSecureItem("currentUser", JSON.stringify(userData));
    setSecureItem("role_id", role_id);

    // Update state
    setAuthTokens({ access_token, refresh_token });
    setUser(userData);

    const resolvedRole = resolveCurrentRole();
    setCurrentRole(resolvedRole);
  };

  // Logout
  const logout = () => {
    const keysToRemove = [
      "accessToken",
      "refreshToken",
      "currentUser",
      "roles",
      "role_id",
      "orgId",
      "unitId",
      "propertyId",
      "accessControl",
      "entities",
    ];

    // 1️⃣ Clear permission memory cache
    resetPermissionCache();

    keysToRemove.forEach((key) => removeSecureItem(key));

    setSecureItem("logout", Date.now().toString());
    setTimeout(() => removeSecureItem("logout"), 1000);

    setUser(null);
    setAuthTokens(null);
    setCurrentRole(null);

    navigate("/sign-in", { replace: true });
  };

  const refreshRole = () => {
    const resolvedRole = resolveCurrentRole();
    setCurrentRole(resolvedRole);
  };

  const updateRole = (roleId) => {
    setSecureItem("role_id", roleId);
    const resolvedRole = resolveCurrentRole();
    setCurrentRole(resolvedRole);
  };

  // Computed values
  const isAuthenticated = !!authTokens?.access_token;
  const isSuperAdmin = currentRole === "super_admin";
  const isAdmin = currentRole === "admin";
  const isManager = currentRole === "manager";
  const isStaff = currentRole === "staff";
  const isUser = currentRole === "tenant";
  const hasRole = currentRole !== null;

  const value = {
    user,
    setUser,
    authTokens,
    setAuthTokens,
    currentRole,
    login,
    logout,
    loading,
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    isManager,
    isStaff,
    isUser,
    refreshRole,
    hasRole,
    updateRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
